<script setup lang="ts">
import type { RegionClickedPayload } from '.';
import type { SystemEnums } from '~/core/entities/SystemEnums';
import type { RegionData } from '~~/core/entities/Region';
import type { Typology } from '~~/core/ports/repositories';

import { cn } from '@/utils';
import {
	FeatureEnum,
	features,
	PropertyTypeEnum,
	PropertyTypePlurals,
	propertyTypes,
	propertyTypesTranslations
} from '~/core/entities/CRMEnums';
import { createFeatureObject } from '~/core/entities/SystemFeatures';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { cleanLocationQueryFromPath } from '~/utils/cleanLocationQueryFromPath';
import { grammarInflection } from '~~/utils';

import TextLink from '../ui/text-link/TextLink.vue';

interface Props extends RegionData {
	class?: string;
	selectedPropertyType?: PropertyTypeEnum;
}

const props = defineProps<Props>();

const emit = defineEmits<{
	(e: 'region-clicked', payload: RegionClickedPayload): void;
	(
		e: 'region-card-clicked',
		payload: { filters: any; destinationUrl: string }
	): void;
}>();

const labelCount = computed(() => {
	if (!props.selectedPropertyType) {
		const label = grammarInflection(props.propertyCount, 'imóvel', 'imóveis');
		return `${props.propertyCount} ${label}`;
	}

	const plural = PropertyTypePlurals[props.selectedPropertyType].toLowerCase();
	const singular =
		propertyTypesTranslations[props.selectedPropertyType].toLowerCase();
	const label = grammarInflection(props.propertyCount, singular, plural);

	return `${props.propertyCount} ${label}`;
});

function getFeatureLink(feature: FeatureEnum, propertyType?: PropertyTypeEnum) {
	if (!feature || !propertyType) return;

	return cleanLocationQueryFromPath(
		PropertyFiltersPresenter.getPathFromFilters(
			PropertyFiltersPresenter.createLocationFilters({
				states: props.state,
				cities: props.city,
				regions: props.region,
				propertyTypes: propertyType,
				features: feature
			})
		)
	);
}

function formatFeatures(typologies: Typology[]) {
	const currentPropertyTypeTypology = typologies.find(
		typology => typology.propertyType === props.selectedPropertyType
	);

	return (
		(currentPropertyTypeTypology?.features as FeatureEnum[] | undefined) || []
	)
		.slice(0, 5)
		.map(feature => ({
			name: createFeatureObject(feature, { features } as SystemEnums)?.name,
			link: getFeatureLink(feature, props.selectedPropertyType)
		}));
}

const formattedFeatures = computed(() =>
	props.selectedPropertyType && !!props.typologies.length
		? formatFeatures(props.typologies) || []
		: []
);

function getPropertyTypeLink(propertyType: string) {
	const propertyTypeObject = propertyTypes.find(
		type => type.identifier === propertyType
	);

	const link = cleanLocationQueryFromPath(
		PropertyFiltersPresenter.getPathFromFilters(
			PropertyFiltersPresenter.createLocationFilters({
				states: props.state,
				cities: props.city,
				regions: props.region,
				propertyTypes: propertyTypeObject?.identifier
			})
		)
	);

	return link;
}

function formatPropertyTypes(types: PropertyTypeEnum[]): {
	type: string;
	link: string;
}[] {
	return types.map(type => ({
		type: propertyTypesTranslations[
			type as keyof typeof propertyTypesTranslations
		],
		link: getPropertyTypeLink(type)
	}));
}

const formattedPropertyTypes = computed(() =>
	props.selectedPropertyType ? [] : formatPropertyTypes(props.topPropertyTypes)
);

const link = computed(() =>
	cleanLocationQueryFromPath(
		PropertyFiltersPresenter.getPathFromFilters(
			PropertyFiltersPresenter.createLocationFilters({
				states: props.state,
				cities: props.city,
				regions: props.region,
				propertyTypes: props.selectedPropertyType
			})
		)
	)
);

const hasFeaturesAndPropertyTypes = computed<boolean>(
	() =>
		!!formattedFeatures.value.length || !!formattedPropertyTypes.value.length
);

function handleRegionClicked() {
	const filters = PropertyFiltersPresenter.createLocationFilters({
		states: props.state,
		cities: props.city,
		regions: props.region,
		propertyTypes: props.selectedPropertyType
	});

	emit('region-clicked', {
		neighborhood_name: props.region,
		destination_url: link.value
	});

	emit('region-card-clicked', {
		filters,
		destinationUrl: link.value
	});
}

function handleFeatureLinkClicked(feature: { name: string; link: string }) {
	const featureEnum = Object.entries(features).find(
		([_, featureObj]) => featureObj.name === feature.name
	)?.[0] as FeatureEnum | undefined;

	const filters = PropertyFiltersPresenter.createLocationFilters({
		states: props.state,
		cities: props.city,
		regions: props.region,
		propertyTypes: props.selectedPropertyType,
		features: featureEnum
	});

	emit('region-card-clicked', {
		filters,
		destinationUrl: feature.link
	});
}

function handlePropertyTypeLinkClicked(type: { type: string; link: string }) {
	const propertyTypeEnum = Object.entries(propertyTypesTranslations).find(
		([_, translation]) => translation === type.type
	)?.[0] as PropertyTypeEnum | undefined;

	const filters = PropertyFiltersPresenter.createLocationFilters({
		states: props.state,
		cities: props.city,
		regions: props.region,
		propertyTypes: propertyTypeEnum
	});

	emit('region-card-clicked', {
		filters,
		destinationUrl: type.link
	});
}
</script>

<template>
	<div class="relative mb-4 flex size-full items-center">
		<div
			:class="
				cn(
					'flex size-full min-h-[280px] cursor-pointer flex-col justify-between rounded-lg bg-secondary p-6 transition hover:border-primary hover:bg-secondary-dark-1',
					props.class
				)
			"
			@click="handleRegionClicked"
		>
			<div class="flex w-full items-center justify-start gap-2">
				<h3 class="text-xl font-medium text-primary">
					{{ props.region }}
				</h3>
				<IconMaterialSymbolsNorthEast class="w-5 text-primary" />
			</div>

			<div
				class="flex flex-col"
				:class="{
					'gap-4': hasFeaturesAndPropertyTypes
				}"
			>
				<p
					class="text-base font-medium text-primary"
					:class="{
						'mb-2': hasFeaturesAndPropertyTypes
					}"
				>
					{{ labelCount }}
				</p>

				<div class="z-50 flex flex-wrap gap-4">
					<div
						v-for="feature in formattedFeatures"
						:key="feature.name"
						class="cursor-pointer"
						@click.prevent.stop="
							feature.name &&
							feature.link &&
							handleFeatureLinkClicked({
								name: feature.name,
								link: feature.link
							})
						"
					>
						<TextLink as="p" variant="default" class="text-sm text-primary">
							{{ feature.name }}
						</TextLink>
					</div>

					<div
						v-for="type in formattedPropertyTypes"
						:key="type.type"
						class="cursor-pointer"
						@click.prevent.stop="handlePropertyTypeLinkClicked(type)"
					>
						<TextLink as="p" variant="default" class="text-sm text-primary">
							{{ type.type }}
						</TextLink>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
